import React, {
  Fragment,
  useCallback,
  useContext,
  useState,
  useEffect,
} from "react";
import makeStyles from "@mui/styles/makeStyles";
import {
  any,
  drop,
  equals,
  flatten,
  gt,
  identity,
  isEmpty,
  length,
  lt,
  map,
  pipe,
  pluck,
  prepend,
  splitAt,
  splitEvery,
  values,
} from "ramda";
import { anyTrue, notBlank } from "../../../util/ext";
import { useAlert, useSelected } from "../OrganizeProvider";
import { OrganizeContext } from "../organize-context";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCopy,
  faAngleRight,
  faAngleDown,
  faAngleUp,
  faFolderPlus,
  faTrashCan,
} from "@fortawesome/pro-regular-svg-icons";
import {
  isAnthology,
  isCollection,
  IncludeDialogTypes,
  allowCopy,
  isContentGroupsHome,
  useView,
} from "../organizeUtil";
import {
  DndContext,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToFirstScrollableAncestor } from "@dnd-kit/modifiers";
import {
  sortableKeyboardCoordinates,
  SortableContext,
} from "@dnd-kit/sortable";
import { mapDisplayOrder, reorderItems } from "../../common/dnd/dndUtils";
import { GroupBrowserContext } from "../OrganizeView";
import {
  useCollectionDelete,
  useCollectionUpdate,
} from "../../collections/collectionServices";
import {
  useAnthologyDelete,
  useAnthologyUpdate,
} from "../../anthologies/anthologyServices";
import { usePermissions } from "../../../util/mediaUtil";

import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Collapse from "@mui/material/Collapse";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import DeleteDialog from "../../common/DeleteDialog";
import DraggableContentGroupCard from "./card/DraggableContentGroupCard";
import EmptyContentGroupCard from "./EmptyContentGroupCard";
import PanelLoadingIndicator from "../../../components/PanelLoadingIndicator";
import SortableContentGroupCard from "../../common/dnd/SortableContentGroupCard";

const useStyles = makeStyles(theme => ({
  accordionRoot: {
    cursor: "default !important",
    padding: 0,
    "&.Mui-focused,&.Mui-focusVisible": {
      backgroundColor: "white",
    },
  },

  accordionDetails: {
    flexDirection: "column",
    padding: 0,
  },

  accordionSummary: {
    minHeight: 90,
    margin: 0,
    "&.Mui-expanded": {
      margin: 0,
    },
  },

  divider: {
    height: 1,
    color: theme.palette.secondary.light,
    backgroundColor: "rgba(0, 0, 0, 0.12)",
    border: "none",
  },

  expandIcon: {
    marginRight: theme.spacing(3),
    width: 15,
    height: 15,
  },

  groups: {
    padding: 0,
    maxHeight: 250,
    overflowY: "auto",
    overflowX: "hidden",
  },
}));

const SelectedCheckbox = ({ expanded, groups }) => {
  const { selectAllContentGroups, selectedContentGroups } = useSelected();

  const [selectedGroups, setSelectedGroups] = useState(selectedContentGroups);

  useEffect(() => {
    setSelectedGroups(selectedContentGroups);
  }, [selectedContentGroups]);

  const [indeterminate, setIndeterminate] = useState(false);
  const [checked, setChecked] = useState(false);
  useEffect(() => {
    const groupsList = expanded.inner
      ? flatten([groups.shown, groups.hidden])
      : groups.shown;

    setIndeterminate(
      gt(length(selectedGroups), 0) &&
        lt(length(selectedGroups), length(groupsList)),
    );
    setChecked(
      gt(length(selectedGroups), 0) &&
        equals(length(selectedGroups), length(groupsList)),
    );
  }, [expanded, groups, selectedGroups]);

  const handleChange = useCallback(
    event => {
      const groupsList = expanded.inner
        ? flatten([groups.shown, groups.hidden])
        : groups.shown;

      selectAllContentGroups(event.target, groupsList);
    },
    [expanded, groups, selectAllContentGroups],
  );

  return (
    <Grid item style={{ paddingLeft: 15 }}>
      <FormControlLabel
        label={
          <Typography variant="h2">
            {`${length(selectedGroups)} selected`}
          </Typography>
        }
        control={
          <Checkbox
            color="primary"
            indeterminate={indeterminate}
            checked={checked}
            onChange={handleChange}
            inputProps={{ "aria-label": "Select" }}
          />
        }
      />
    </Grid>
  );
};

const ContentGroupsPanel = React.memo(function ContentGroupsPanel(props) {
  const {
    addToParent,
    reordering,
    contentGroups,
    filterMenu,
    handleContentGroupsPageChange,
    hasMore,
    hideTotal,
    loading,
    parent,
    removeFromParent,
    searchBar,
    setContentGroups,
    showAlert,
    title,
    totalGroups,
    types,
  } = props;
  const classes = useStyles();
  const view = useView();
  const alert = useAlert();
  const groupBrowser = useContext(GroupBrowserContext);

  //-- Manage Content Groups
  const [groups, setGroups] = useState([]);
  const [hiddenGroups, setHiddenGroups] = useState([]);
  useEffect(() => {
    const cards = reordering
      ? contentGroups
      : prepend({ id: "emptyCard" }, contentGroups);
    const groups = splitEvery(4, cards);
    const [shown, hidden] = splitAt(2, groups);
    setGroups(shown);
    setHiddenGroups(hidden);
  }, [reordering, contentGroups]);

  const {
    selectedContentGroups,
    removeAllSelectedContentGroups,
  } = useSelected();
  const context = useContext(OrganizeContext);
  const openIncludeDialog = action => event => {
    context.updateIncludeDialog({
      open: true,
      action: action,
      activeType: IncludeDialogTypes.contentGroup,
      selected: selectedContentGroups,
    });
  };

  //-- Manage Accordion State
  const handleOuterChange = () =>
    groupBrowser.setExpanded(prev => ({
      ...prev,
      contentGroups: {
        inner: false,
        outer: !prev.contentGroups.outer,
      },
    }));
  const handleInnerChange = () =>
    groupBrowser.setExpanded(prev => ({
      ...prev,
      contentGroups: {
        ...prev.contentGroups,
        inner: !prev.contentGroups.inner,
      },
    }));

  //-- Manage Sort
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = event => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setContentGroups(reorderItems({ active, over, mapper: mapDisplayOrder }));
    }
  };

  //-- Manage Content Group Deletion
  const deleteAnthology = useAnthologyDelete();
  const deleteCollection = useCollectionDelete();
  const [showDelete, setShowDelete] = useState(true);

  const { permissions, PermissionComponents } = usePermissions(
    selectedContentGroups,
  );
  useEffect(() => {
    if (notBlank(permissions)) {
      // if any content group cannot be deleted, hide delete button; must delete from OtherActions menu
      const showDelete = pipe(values, pluck("deletable"), anyTrue)(permissions);
      setShowDelete(showDelete);
    }
  }, [permissions]);

  const updateAnthology = useAnthologyUpdate();
  const updateCollection = useCollectionUpdate();
  const handleUndo = previousGroups => async () => {
    groupBrowser.setLoading(true);
    try {
      await Promise.all(
        map(async group => {
          const mutation = {
            resourceIds: { id: group.id },
            data: { status: group?.status ?? null },
          };
          if (isAnthology(group)) {
            return updateAnthology.mutateAsync(mutation);
          }
          if (isCollection(group)) {
            return updateCollection.mutateAsync(mutation);
          }
        }, previousGroups),
      );
    } catch (err) {
      showAlert("error", [err.message]);
    }
    groupBrowser.setLoading(false);
  };

  const handleDelete = async () => {
    await Promise.all(
      map(async group => {
        if (isAnthology(group)) {
          return deleteAnthology.mutateAsync({
            anthology: group,
          });
        }

        if (isCollection(group)) {
          return deleteCollection.mutateAsync({
            collection: group,
          });
        }
      }, selectedContentGroups),
    );
    removeAllSelectedContentGroups();
    alert.update({
      actionButtonLabel: "undo",
      showAction: true,
      onAlertClick: handleUndo(selectedContentGroups),
    });
  };

  /**
   * PermissionComponents renders PermissionChecker on each selected content group
   * which calls useMediaPermissions on each piece of media
   */
  return (
    <Fragment>
      {PermissionComponents}
      <Accordion expanded={groupBrowser.expanded.contentGroups.outer}>
        <AccordionSummary
          aria-label={`Expand ${title}`}
          classes={{
            root: classes.accordionRoot,
            content: classes.accordionSummary,
          }}
        >
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid item xs>
              <Grid container alignItems="center">
                <IconButton
                  className={classes.expandIcon}
                  onClick={handleOuterChange}
                  size="large"
                >
                  <FontAwesomeIcon
                    icon={
                      groupBrowser.expanded.contentGroups.outer
                        ? faAngleDown
                        : faAngleRight
                    }
                  />
                </IconButton>

                <Typography variant="h2">
                  {title || "Content Groups"}{" "}
                  {hideTotal ? "" : `(${totalGroups})`}
                </Typography>

                {notBlank(selectedContentGroups) && (
                  <Fragment>
                    <SelectedCheckbox
                      expanded={groupBrowser.expanded.contentGroups}
                      groups={{
                        shown: drop(1, flatten(groups)),
                        hidden: flatten(hiddenGroups),
                      }}
                    />

                    {!isContentGroupsHome(view) && (
                      <Tooltip title="Move" placement="bottom">
                        <IconButton
                          onClick={openIncludeDialog("move")}
                          size="large"
                        >
                          <FontAwesomeIcon icon={faFolderPlus} />
                        </IconButton>
                      </Tooltip>
                    )}

                    {allowCopy(view) && (
                      <Tooltip title="Copy" placement="bottom">
                        <IconButton
                          onClick={openIncludeDialog("copy")}
                          size="large"
                        >
                          <FontAwesomeIcon icon={faCopy} />
                        </IconButton>
                      </Tooltip>
                    )}

                    {showDelete && (
                      <DeleteDialog
                        handleDelete={handleDelete}
                        count={length(selectedContentGroups)}
                        renderButton={onDelete => (
                          <Tooltip title="Delete">
                            <IconButton onClick={onDelete} size="large">
                              <FontAwesomeIcon icon={faTrashCan} />
                            </IconButton>
                          </Tooltip>
                        )}
                      />
                    )}
                  </Fragment>
                )}
              </Grid>
            </Grid>

            {groupBrowser.expanded.contentGroups.outer && (
              <Grid
                item
                container
                xs
                justifyContent="flex-end"
                alignItems="center"
              >
                {notBlank(searchBar) && (
                  <Grid item xs>
                    {searchBar}
                  </Grid>
                )}
                {notBlank(filterMenu) && (
                  <Grid
                    item
                    xs={reordering ? true : "auto"}
                    container={reordering}
                    justifyContent={reordering ? "flex-end" : "flex-start"}
                  >
                    {filterMenu}
                  </Grid>
                )}
              </Grid>
            )}
          </Grid>
        </AccordionSummary>
        <AccordionDetails classes={{ root: classes.accordionDetails }}>
          {notBlank(groups) && (
            <DndContext
              onDragEnd={handleDragEnd}
              sensors={sensors}
              collisionDetection={closestCorners}
              modifiers={[restrictToFirstScrollableAncestor]}
            >
              <SortableContext items={contentGroups} disabled={!reordering}>
                <Grid className={classes.groups} container>
                  {any(identity, [
                    loading,
                    groupBrowser.loading,
                    deleteAnthology.isPending,
                    deleteCollection.isPending,
                  ]) && (
                    <PanelLoadingIndicator transparent={!isEmpty(groups)} />
                  )}

                  {groups.map(row => (
                    <Grid
                      key={`content-group-row-${Math.random()}`}
                      container
                      style={{ padding: "2.5px 0" }}
                    >
                      {row.map(group => (
                        <Grid
                          key={`content-group-card-${group.id}`}
                          item
                          xs={3}
                          style={{ padding: "0 2.5px" }}
                        >
                          {equals(group.id, "emptyCard") ? (
                            <EmptyContentGroupCard
                              parent={parent}
                              addToParent={addToParent}
                              parentPrevMediaListCount={totalGroups}
                            />
                          ) : reordering ? (
                            <SortableContentGroupCard
                              key={group.id}
                              group={group}
                            />
                          ) : (
                            <DraggableContentGroupCard
                              key={group.id}
                              group={group}
                              parent={parent}
                              parentPrevMediaList={contentGroups}
                              showAlert={showAlert}
                              removeFromParent={removeFromParent}
                            />
                          )}
                        </Grid>
                      ))}
                    </Grid>
                  ))}

                  <Collapse
                    in={groupBrowser.expanded.contentGroups.inner}
                    style={{ width: "100%" }}
                  >
                    {notBlank(hiddenGroups) &&
                      hiddenGroups.map(row => (
                        <Grid
                          key={`content-group-row-hidden-${Math.random()}`}
                          container
                          style={{ padding: "2.5px 0" }}
                        >
                          {row.map(group => (
                            <Grid
                              key={`content-group-card-${group.id}`}
                              item
                              xs={3}
                              style={{ padding: "0 2.5px" }}
                            >
                              {reordering ? (
                                <SortableContentGroupCard
                                  key={group.id}
                                  group={group}
                                />
                              ) : (
                                <DraggableContentGroupCard
                                  key={group.id}
                                  group={group}
                                  parent={parent}
                                  parentPrevMediaList={contentGroups}
                                  showAlert={showAlert}
                                  removeFromParent={removeFromParent}
                                />
                              )}
                            </Grid>
                          ))}
                        </Grid>
                      ))}

                    {hasMore && notBlank(types) && (
                      <Grid
                        item
                        xs
                        container
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Button
                          color="primary"
                          onClick={handleContentGroupsPageChange}
                        >
                          More
                        </Button>
                      </Grid>
                    )}
                  </Collapse>
                </Grid>
              </SortableContext>
            </DndContext>
          )}

          {notBlank(hiddenGroups) && notBlank(types) ? (
            <Grid container alignItems="center">
              <Grid item xs>
                <hr className={classes.divider} />
              </Grid>

              <Grid item>
                <Button
                  color="primary"
                  startIcon={
                    <FontAwesomeIcon
                      icon={
                        groupBrowser.expanded.contentGroups.inner
                          ? faAngleUp
                          : faAngleDown
                      }
                    />
                  }
                  onClick={handleInnerChange}
                >
                  {groupBrowser.expanded.contentGroups.inner
                    ? "Collapse"
                    : "Expand"}
                </Button>
              </Grid>

              <Grid item xs>
                <hr className={classes.divider} />
              </Grid>
            </Grid>
          ) : (
            <hr className={classes.divider} style={{ margin: "20px 0" }} />
          )}
        </AccordionDetails>
      </Accordion>
      {!groupBrowser.expanded.contentGroups.outer && (
        <hr className={classes.divider} />
      )}
    </Fragment>
  );
});

export default ContentGroupsPanel;
